import parse from 'html-react-parser';
import { renderToStaticMarkup } from 'react-dom/server';
import clozeMathService from '../services/ClozeMathService';
import htmlService from '../services/HtmlService';
import promptService from '../services/PromptService';
import questionService from '../services/QuestionService';
import lessonManager from '../managers/LessonManager';

import { ClozeBehavior } from './ClozeBehavior';

import utilsService from '../services/UtilsService';

export class ClozeComboBehavior extends ClozeBehavior {
  setResponseData = (data, _responseAnswer, model, { dataId, lessonElementState, isCustomBlankSection } = {}) => {
    const prompt = promptService.getCurrentPrompt({
      dataId,
      lessonElementState,
      model,
      promptType: 'anyModelPrompt'
    });

    if (prompt && prompt.rangeType === 'cloze') {
      if (!lessonElementState.currentResponseAnswer.userInputMap) {
        questionService.updateUserInputMap({
          model,
          lessonElementState
        });
      } else if (htmlService.isHtmlResponseFormat(model) || isCustomBlankSection) {
        lessonElementState.currentResponseAnswer.userInputMap.set(dataId, {
          dataId,
          text: data
        });
      } else {
        // `responseFormat` is 'text' or 'numeric' and using the native input element
        if (model.responseFormat === 'numeric') {
          if (utilsService.ensureNumericInput(data.target.value)) {
            lessonElementState.currentResponseAnswer.userInputMap.set(dataId, {
              dataId,
              text: data?.target?.value
            });
          }
        } else {
          lessonElementState.currentResponseAnswer.userInputMap.set(dataId, {
            dataId,
            text: data?.target?.value
          });
        }
      }
    } else {
      const answerId = questionService.getPickAnswerId(data);
      const payload = {
        ...data.originalData,
        dataId,
        answerId,
        text: (model?.responseFormat === 'html') ? data.viewLabel : data.label,
      };
      if (!lessonElementState.currentResponseAnswer.userInputMap) {
        const userInputMap = questionService.updateUserInputMap({
          model,
          lessonElementState
        });
        userInputMap.set(dataId, payload);
      } else {
        lessonElementState.currentResponseAnswer.userInputMap.set(dataId, payload);
      }
    }
    lessonManager.stampResponsePublisherTranslationId(lessonElementState?.currentResponseAnswer);
  }

  setCorrectAnswer = (state, model) => {
    const userInputMap = new Map();
    for (const modelValidationPrompt of (model?.validation?.prompts || [])) {
      if (modelValidationPrompt && modelValidationPrompt.rangeType === 'clozeMultichoice') {
        const correctAnswer = modelValidationPrompt.answers.find((answer) => answer.isCorrect);
        userInputMap.set(modelValidationPrompt.id, {
          dataId: modelValidationPrompt.id,
          answerId: correctAnswer?.id,
          text: correctAnswer ? correctAnswer.text : ''
        });
      } else if (modelValidationPrompt && modelValidationPrompt.rangeType === 'cloze') {
        let answerText;
        if (promptService.isUserInputAlwaysCorrectIfFoundInModelPromptAnswers(model, modelValidationPrompt)) {
          answerText = modelValidationPrompt?.answers?.[0]?.text || '';
        } else {
          answerText = modelValidationPrompt?.answers?.find((answer) => answer.isCorrect)?.text || '';
        }

        if (answerText && ((model && !utilsService.isNullOrEmpty(model.mathText)) || model?.responseFormat === 'math')) {
          answerText = renderToStaticMarkup(parse(answerText, {
            replace: (domNode) => clozeMathService.appendDataIdToMathDomNodeThenConvertToMencloseIfApplicable(domNode, modelValidationPrompt.id, model)
          }));
        }
        const dataId = modelValidationPrompt?.id;
        userInputMap.set(dataId, {
          dataId,
          text: answerText || (model.mathText && renderToStaticMarkup(
            clozeMathService.renderClozeMathBlankPlaceholder(dataId, model, '?')
          )) || '',
          isCorrect: true
        });
      }
    }

    state.setCurrentResponse(utilsService.safeMobxClone({
      userInputMap, lessonElementId: model.lessonElementId
    }));
  }

  isAnswerCorrect = (dataId, lessonElementState, model) => {
    const userInputMap = lessonElementState?.currentResponseAnswer.userInputMap || questionService.updateUserInputMap({
      model,
      responseAnswer: lessonElementState.currentResponseAnswer,
      lessonElementState
    });
    const currentUserInput = userInputMap?.get(dataId);

    const prompt = promptService.getCurrentPrompt({
      dataId,
      lessonElementState,
      model,
      promptType: 'anyModelPrompt'
    });
    
    if (prompt?.rangeType === 'cloze') {
      if (lessonElementState?.currentResponseAnswer?.answerResults) {
        const matchingResults = lessonElementState.currentResponseAnswer.answerResults
          .filter(result => result.answerId === dataId && result.match !== undefined);
        if (matchingResults.length) {
          return matchingResults.some(result => result.match);
        }
      }
    } else if (prompt?.rangeType === 'clozeMultichoice') {
      if (lessonElementState?.currentResponseAnswer?.answerResults) {
        const answerResult = lessonElementState.currentResponseAnswer.answerResults.find(result => result.answerId === dataId);
        if (answerResult && answerResult.match !== undefined) {
          return answerResult.match;
        }
      }
    }
    
    // Fallback to existing logic for backward compatibility
    // Stripping paragraph tags here so we can trim the string.
    const currentUserInputValue = currentUserInput?.text;

    const result = currentUserInput?.isCorrect || promptService.isCorrectPromptAnswerStatus({
      dataId, currentUserInput, currentUserInputValue, model
    });
    return result;
  }

  resetStudentAnswer = (state, _validation) => {
    state.setCurrentResponse(utilsService.safeMobxClone(state.cachedResponseAnswer));
  }
}

export default new ClozeComboBehavior();
